import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import type { ThemeMode } from '@/store/uiStore';

export const SYSTEM_BAR_COLORS = {
  dark: {
    background: '#0B101A',
    metaThemeColor: '#0B101A',
    appleStyle: 'black-translucent',
    // In Capacitor: Style.Dark = Light (white) text/icons for dark backgrounds
    statusBarStyle: Style.Dark,
    isAppearanceLightStatusBars: false, // For Android native WindowInsetsController: false = light icons
  },
  light: {
    background: '#FFFFFF',
    metaThemeColor: '#FFFFFF',
    appleStyle: 'default',
    // In Capacitor: Style.Light = Dark (black) text/icons for light backgrounds
    statusBarStyle: Style.Light,
    isAppearanceLightStatusBars: true, // For Android native WindowInsetsController: true = dark icons
  },
} as const;

/**
 * Resolves the effective theme mode ('light' or 'dark') given a ThemeMode ('light' | 'dark' | 'system').
 */
export function resolveEffectiveTheme(mode?: ThemeMode | string | null): 'light' | 'dark' {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark'; // VYORA default
}

/**
 * Synchronizes the native Android/iOS system status bar, safe area background color,
 * web theme-color meta tag, and document styling to match the effective theme.
 */
export async function syncSystemBars(themeMode?: ThemeMode | string | null): Promise<void> {
  const effectiveTheme = resolveEffectiveTheme(themeMode);
  const config = SYSTEM_BAR_COLORS[effectiveTheme];

  if (typeof document !== 'undefined') {
    // 1. Synchronize HTML attributes & Color Scheme
    document.documentElement.setAttribute('data-theme', effectiveTheme);
    document.documentElement.style.colorScheme = effectiveTheme;

    // 2. Dynamic <meta name="theme-color">
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.setAttribute('name', 'theme-color');
      document.head.appendChild(themeMeta);
    }
    themeMeta.setAttribute('content', config.metaThemeColor);

    // 3. Dynamic <meta name="apple-mobile-web-app-status-bar-style">
    let appleMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (!appleMeta) {
      appleMeta = document.createElement('meta');
      appleMeta.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
      document.head.appendChild(appleMeta);
    }
    appleMeta.setAttribute('content', config.appleStyle);
  }

  // 4. Native Capacitor StatusBar Synchronization
  if (Capacitor.isNativePlatform()) {
    try {
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: config.background });
      // Style.Dark -> Light icons for Dark background (#0B101A)
      // Style.Light -> Dark icons for Light background (#FFFFFF)
      await StatusBar.setStyle({ style: config.statusBarStyle });
    } catch {
      // Gracefully ignore if StatusBar plugin or native bridge is unsupported
    }
  }
}
